// -----------------------------------------------------------------------------
// A3.1 Export PDF (options) → A3.1.1 Download PDFs (one row per PDF). Part 5, rounds 20–21.
//   * Components ticked → one PDF each; "→ V03" = the version a Download will take (pdf.ts nextVersion).
//   * Download (component) = rpc issues_publish: the version is taken at the click, even if the print box is then
//     cancelled (Kyle 13/Oct/26); the row turns ✓ Downloaded and offers Print again (same version, no new number).
//   * Round 22 #2: every Download not yet clicked is orange (primary), not only the next one.
//   * Round 22 #3: "Include signing page" starts unticked → versions are "Published, not sent" unless ticked.
//   * Master summary (P0): own PDF, date only, no version; printed from the A3 data already on screen.
// Parameters flow: ExportDialog → options → DownloadDialog → issues_publish(args) → PdfData → printer.printPdf(ComponentPdf).
// -----------------------------------------------------------------------------
import { loadPrintSnaps, resolveCoverPictures } from "../print/snaps";
import { useEffect, useRef, useState } from "react";
import type { IncludeIssues, IssuesOverview, PdfData, PrintContext } from "../../../core/api/types";
import { rpc } from "../../../core/api/backend";
import { DATE_HINT_ROW, formatDate } from "../../../core/format";
import { DateInput, InfoIcon, Modal, Seg } from "../../../core/ui/kit";
import { ComponentPdf, MasterPdf } from "../print/PrintPages";
import { printPdf } from "../print/printer";
import { masterFileName, nextVersion, pdfFileName } from "../print/pdf";
import { Icon } from "../../../core/ui/icons";

export const EXPORT_TIPS = {
  export: ["Each ticked component becomes its own PDF and its own next version.", "Export… opens the list of PDFs (A3.1.1); download each one from there.",
    "The file name is filled in, e.g. 2026-09-26 LZ-Low zone level 14–43-V02.", "Pages are A4 with their own margins; browser headers and footers are off."],
  // Round 22 #3 (Kyle 17/Oct/26): off by default — matches board tip A3.1 #2.
  signing: ["Off by default: tick it when this version goes out for sign-off.", "Ticked: each exported version is marked Awaiting sign-off (amber); unticked: Published, not sent (white)."],
  master: ["Exported on its own.", "Date is automatic."],
  download: ["Download opens the browser's print box for that PDF: choose Save as PDF.", "Clicking Download takes the version number, even if the print box is then cancelled.",
    "Print again reprints a downloaded PDF with the same version; no new number.", "The master summary carries the date only, no version.", "Rows not downloaded take no number."],
};

export interface ExportOptions {
  prefixes: string[]; disciplines: string[]; signing: boolean; issues: IncludeIssues; reportDate: string; master: boolean;
}

/** Tri-state checkbox (All components). */
function TriCheck({ all, some, onChange, label }: { all: boolean; some: boolean; onChange: (v: boolean) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = some && !all; }, [all, some]);
  return <input ref={ref} type="checkbox" checked={all} aria-label={label} onChange={(e) => onChange(e.target.checked)} />;
}

/** A3.1 — pick what to export. */
export function ExportDialog({ ov, onClose, onExport }: { ov: IssuesOverview; onClose: () => void; onExport: (o: ExportOptions) => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const [discs, setDiscs] = useState<string[]>(ov.disciplines.map((d) => d.code));
  const [signing, setSigning] = useState(false);   // round 22 #3: unticked by default
  const [issues, setIssues] = useState<IncludeIssues>("open");
  const [when, setWhen] = useState<"today" | "pick">("today");
  const [date, setDate] = useState<string | null>(null);
  const [dateOk, setDateOk] = useState(true);
  const [master, setMaster] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const comps = ov.components;
  const n = picked.length + (master ? 1 : 0);
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const go = () => {
    if (when === "pick" && (!date || !dateOk)) { setErr(`Date of report: ${DATE_HINT_ROW}`); return; }
    onExport({ prefixes: comps.filter((c) => picked.includes(c.prefix)).map((c) => c.prefix), disciplines: discs, signing, issues,
      reportDate: when === "pick" && date ? date : ov.today, master });
  };
  return (
    <Modal title="Export PDF" info={EXPORT_TIPS.export} onClose={onClose} className="export-modal natural">
      <p className="export-k">Components</p>
      <div className="export-box" data-testid="export-components">
        <label className="export-all"><TriCheck all={picked.length === comps.length && comps.length > 0} some={picked.length > 0} label="All components"
          onChange={(v) => setPicked(v ? comps.map((c) => c.prefix) : [])} />
          All components · <span className="muted">{picked.length} of {comps.length} ticked</span></label>
        <div className="export-grid">{comps.map((c) => (
          <label key={c.id} className="export-row" data-export={c.prefix}>
            <input type="checkbox" checked={picked.includes(c.prefix)} onChange={() => setPicked(toggle(picked, c.prefix))} aria-label={`${c.prefix} ${c.name}`} />
            <span className="code">{c.prefix}</span><span className="ell">{c.name}</span>
            <span className={`export-next${picked.includes(c.prefix) ? " on" : ""}`}>→ {nextVersion(c.signoff.latest)}</span>
          </label>))}
        </div>
      </div>
      <p className="export-k">Disciplines</p>
      <div className="row gap6 wrap" style={{ marginBottom: 14 }}>{ov.disciplines.map((d) => {
        const on = discs.includes(d.code);
        return <button key={d.code} type="button" className={`disc-tog${on ? " on" : ""}`} aria-pressed={on} onClick={() => setDiscs(toggle(discs, d.code))}
          style={on ? { background: d.colour + "1a", borderColor: d.colour + "66", color: d.colour } : undefined}><i style={{ background: on ? d.colour : undefined }} />{d.code}</button>;
      })}</div>
      <p className="export-k">Pages</p>
      <label className="row gap8 export-check"><input type="checkbox" checked={signing} onChange={(e) => setSigning(e.target.checked)} />Include signing page <InfoIcon tip={EXPORT_TIPS.signing} /></label>
      <div className="export-opts">
        <div><p className="export-k">Issues to include</p>
          <Seg<IncludeIssues> label="Issues to include" value={issues} onChange={setIssues}
            options={[{ value: "all", label: "All" }, { value: "open", label: "Open" }, { value: "critical", label: "Critical" }]} /></div>
        <div><p className="export-k">Date of report</p>
          <div className="row gap8">
            <Seg<"today" | "pick"> label="Date of report" value={when} onChange={(v) => { setWhen(v); setErr(null); }}
              options={[{ value: "today", label: `Today (${formatDate(ov.today)})` }, { value: "pick", label: "Pick date" }]} />
            <div className={`export-date${when === "pick" ? "" : " off"}`}>
              {when === "pick" ? <DateInput value={date} onChange={(v) => { setDate(v); setErr(null); }} onValidity={setDateOk} ariaLabel="Date of report" invalid={!!err} />
                : <input disabled placeholder="DD/Mmm/YY" aria-label="Date of report (pick date first)" />}
            </div>
          </div></div>
      </div>
      <label className="row gap8 export-check sep"><input type="checkbox" checked={master} onChange={(e) => setMaster(e.target.checked)} />Master summary <InfoIcon tip={EXPORT_TIPS.master} /></label>
      {err && <p className="error-inline" role="alert">{err}</p>}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={n === 0} onClick={go}>Export {n} PDF{n === 1 ? "" : "s"}…</button></div>
    </Modal>
  );
}

type Row = { key: string; prefix: string | null; name: string; file: string; version: string; done: boolean; data?: PdfData };

/** A3.1.1 — one row per PDF; Download takes the version and opens the print box. */
export function DownloadDialog({ code, ov, opts, onClose }: { code: string; ov: IssuesOverview; opts: ExportOptions; onClose: (changed: boolean) => void }) {
  const [rows, setRows] = useState<Row[]>(() => [
    ...(opts.master ? [{ key: "master", prefix: null, name: "Master summary", file: masterFileName(opts.reportDate, code), version: "–", done: false }] : []),
    ...opts.prefixes.map((p) => {
      const c = ov.components.find((x) => x.prefix === p)!;
      const v = nextVersion(c.signoff.latest);
      return { key: p, prefix: p, name: c.name, file: pdfFileName(p, c.name, v, opts.reportDate, code), version: v, done: false };
    }),
  ]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const ctx = useRef<PrintContext | null>(null);
  const changed = rows.some((r) => r.prefix && r.done);
  const context = async () => (ctx.current ??= await resolveCoverPictures(await rpc("issues_print_context", { code })));   // v0.7.0: stored pictures → URLs
  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const download = async (r: Row, again = false) => {
    setBusy(r.key); setErr(null);
    try {
      const c = await context();
      if (!r.prefix) {
        set(r.key, { done: true });
        await printPdf(r.file, <MasterPdf ov={ov} ctx={c} reportDate={opts.reportDate} />);
      } else {
        let data = r.data;
        if (!again || !data) {
          const res = await rpc("issues_publish", { code, prefix: r.prefix, with_signing_page: opts.signing, report_date: opts.reportDate,
            include: { issues: opts.issues, disciplines: opts.disciplines } });
          data = res.data;
          set(r.key, { done: true, data, version: res.version, file: pdfFileName(r.prefix, r.name, res.version, opts.reportDate, code) });
        }
        const snaps = await loadPrintSnaps(code, r.prefix);
        await printPdf(pdfFileName(r.prefix, r.name, data.version, opts.reportDate, code), <ComponentPdf data={data} ctx={c} snaps={snaps} />);
      }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  };
  const done = rows.filter((r) => r.done).length;
  return (
    <Modal title="Download PDFs" info={EXPORT_TIPS.download} onClose={() => onClose(changed)} className="download-modal">
      <div className="dl-grid dl-head"><span>PDF</span><span>File name</span><span>Version</span><span /></div>
      {rows.map((r) => (
        <div key={r.key} className="dl-grid dl-row" data-download={r.prefix ?? "master"}>
          <span className="ell">{r.prefix && <><span className="code">{r.prefix}</span>&nbsp;</>}{r.name}</span>
          <span className="ell muted small">{r.file}.pdf</span>
          <span className={`dl-ver${r.done && r.prefix ? " ok" : ""}`}>{r.version}</span>
          <span className="dl-act">{r.done
            ? <><span className="ok small"><Icon name="check" /> Downloaded</span><button className="btn link accent" disabled={!!busy} onClick={() => download(r, true)}>Print again</button></>
            : <button className="btn primary" disabled={!!busy} onClick={() => download(r)}>
                {busy === r.key ? "Preparing…" : "Download"}</button>}</span>
        </div>))}
      {err && <p className="error-inline" role="alert">{err}</p>}
      <div className="modal-foot sep dl-foot"><span className="muted small" data-testid="download-count">{done} of {rows.length} downloaded</span><span className="grow" />
        <button className="btn" onClick={() => onClose(changed)}>Close</button></div>
    </Modal>
  );
}
